import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Header } from '@/components/Header';
import { ImageUpload } from '@/components/ImageUpload';
import { FormatSelector, FormatType, getFormatDimensions } from '@/components/FormatSelector';
import { CreativeContextInput } from '@/components/CreativeContextInput';
import { OutputPanel, FailedFormat, FailedFormatReason } from '@/components/OutputPanel';
import { NoCreditsModal } from '@/components/NoCreditsModal';
import { Button } from '@/components/ui/button';
import { Sparkles, Loader2, ChevronDown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { fitImageToExactSizeIfNeeded } from '@/lib/fitImageToExactSize';
import { useIsMobile } from '@/hooks/use-mobile';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { tracker } from '@/lib/analytics';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface GeneratedImage {
  format: FormatType;
  url: string;
}

export default function Studio() {
  const { t } = useLanguage();
  const [sourceImage, setSourceImage] = useState<File | null>(null);
  const [sourcePreview, setSourcePreview] = useState<string | null>(null);
  const [selectedFormats, setSelectedFormats] = useState<FormatType[]>([]);
  const [creativeContext, setCreativeContext] = useState('');
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingFormats, setLoadingFormats] = useState<FormatType[]>([]);
  const [showNoCreditsModal, setShowNoCreditsModal] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [outputExpanded, setOutputExpanded] = useState(true);
  const [failedFormats, setFailedFormats] = useState<FailedFormat[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const { profile, refreshProfile } = useAuth();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!profile) {
      refreshProfile();
    }
  }, [profile, refreshProfile]);

  const handleImageSelect = (file: File, preview: string) => {
    setSourceImage(file);
    setSourcePreview(preview);
    setGeneratedImages([]);
    setFailedFormats([]);
  };

  const handleClearImage = () => {
    setSourceImage(null);
    setSourcePreview(null);
    setGeneratedImages([]);
    setFailedFormats([]);
  };

  const handleUpgrade = async () => {
    setIsUpgrading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-payment-link');
      if (error) throw error;
      if (data?.url) {
        tracker.track('purchase_click');
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Error creating payment link:', error);
      toast({
        title: t('studio', 'errorPaymentLink'),
        description: t('studio', 'tryAgainLater'),
        variant: 'destructive',
      });
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleGenerateClick = () => {
    if (!sourceImage || selectedFormats.length === 0) {
      toast({
        title: t('studio', 'selectImageAndFormat'),
        variant: 'destructive',
      });
      return;
    }

    const credits = profile?.credits ?? 0;
    if (credits < selectedFormats.length) {
      setShowNoCreditsModal(true);
      return;
    }

    if (selectedFormats.length > 2) {
      setShowConfirmDialog(true);
    } else {
      executeGeneration();
    }
  };

  const executeGeneration = async () => {
    if (!sourceImage || selectedFormats.length === 0) return;

    setIsGenerating(true);
    setLoadingFormats([...selectedFormats]);
    setGeneratedImages([]);
    setFailedFormats([]);

    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve) => {
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(sourceImage);
      });
      const base64Image = await base64Promise;

      const invokeFormat = async (format: FormatType) => {
        const dims = getFormatDimensions(format);
        const body: Record<string, any> = {
          image: base64Image,
          format,
          context: creativeContext,
        };
        if (dims) {
          body.width = dims.width;
          body.height = dims.height;
        }
        const { data, error } = await supabase.functions.invoke('adapt-image', { body });
        if (error) throw error;
        return { format, data };
      };

      const results = await Promise.allSettled(
        selectedFormats.map((format) => invokeFormat(format))
      );

      for (const result of results) {
        if (result.status === 'fulfilled') {
          const { format, data } = result.value;
          if (data?.image) {
            const dims = getFormatDimensions(format);
            if (dims) {
              const normalized = await fitImageToExactSizeIfNeeded(data.image, dims.width, dims.height);
              setGeneratedImages((prev) => [...prev, { format, url: normalized }]);
            } else {
              setGeneratedImages((prev) => [...prev, { format, url: data.image }]);
            }
          }
          setLoadingFormats((prev) => prev.filter((f) => f !== format));
        } else {
          const failedFormat = selectedFormats[results.indexOf(result)];
          console.error(`Error generating ${failedFormat}:`, result.reason);
          setLoadingFormats((prev) => prev.filter((f) => f !== failedFormat));
          
          const errorMessage = String(result.reason?.message || '');
          let reason: FailedFormatReason = 'unknown';
          if (errorMessage.includes('503') || errorMessage.includes('overloaded')) {
            reason = 'overloaded';
          } else if (errorMessage.includes('fetch') || errorMessage.includes('abort') || errorMessage.includes('timeout')) {
            reason = 'timeout';
          }
          setFailedFormats((prev) => [...prev, { format: failedFormat, reason }]);
        }
      }

      await refreshProfile();
      
      const successCount = results.filter(r => r.status === 'fulfilled').length;
      if (successCount > 0) {
        tracker.track('image_adapt', { formats: selectedFormats.join(','), count: selectedFormats.length });
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast({
        title: t('studio', 'errorGeneratingImages'),
        description: t('studio', 'tryAgainLater'),
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
      setLoadingFormats([]);
    }
  };

  const handleRetryFormat = async (format: FormatType) => {
    if (!sourceImage) return;
    const credits = profile?.credits ?? 0;
    if (credits < 1) {
      setShowNoCreditsModal(true);
      return;
    }

    setFailedFormats((prev) => prev.filter((f) => f.format !== format));
    setLoadingFormats((prev) => [...prev, format]);
    setIsGenerating(true);

    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve) => {
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(sourceImage);
      });
      const base64Image = await base64Promise;

      const dims = getFormatDimensions(format);
      const body: Record<string, any> = { image: base64Image, format, context: creativeContext };
      if (dims) {
        body.width = dims.width;
        body.height = dims.height;
      }

      const { data, error } = await supabase.functions.invoke('adapt-image', { body });
      if (error) throw error;

      if (data?.image) {
        if (dims) {
          const normalized = await fitImageToExactSizeIfNeeded(data.image, dims.width, dims.height);
          setGeneratedImages((prev) => [...prev, { format, url: normalized }]);
        } else {
          setGeneratedImages((prev) => [...prev, { format, url: data.image }]);
        }
      }
      await refreshProfile();
    } catch (error) {
      console.error(`Retry error for ${format}:`, error);
      const errorMessage = String((error as any)?.message || '');
      let reason: FailedFormatReason = 'unknown';
      if (errorMessage.includes('503') || errorMessage.includes('overloaded')) reason = 'overloaded';
      else if (errorMessage.includes('fetch') || errorMessage.includes('abort') || errorMessage.includes('timeout')) reason = 'timeout';
      setFailedFormats((prev) => [...prev, { format, reason }]);
    } finally {
      setLoadingFormats((prev) => prev.filter((f) => f !== format));
      setIsGenerating(false);
    }
  };

  const totalCost = selectedFormats.length;
  const canGenerate = sourceImage && selectedFormats.length > 0 && !isGenerating;
  const creditWord = totalCost === 1 ? t('studio', 'credit') : t('studio', 'credits');
  const imageWord = generatedImages.length === 1 ? t('studio', 'image') : t('studio', 'images');

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      <Header onUpgrade={handleUpgrade} isUpgrading={isUpgrading} />
      
      <main className={`flex-1 container mx-auto px-4 py-4 overflow-hidden ${isMobile ? 'pb-20' : ''}`}>
        <div className={`${isMobile ? 'flex flex-col gap-4 h-full overflow-auto' : 'grid lg:grid-cols-2 gap-6 h-full'}`}>
          {/* Left Panel - Input */}
          <div className="flex flex-col gap-4 min-h-0 overflow-auto">
            <div className="flex-shrink-0">
              <h2 className="text-lg font-semibold mb-2">{t('studio', 'sourceImage')}</h2>
              <ImageUpload
                onImageSelect={handleImageSelect}
                preview={sourcePreview}
                onClear={handleClearImage}
              />
            </div>

            <FormatSelector
              selected={selectedFormats}
              onChange={setSelectedFormats}
              disabled={isGenerating}
            />

            <CreativeContextInput
              value={creativeContext}
              onChange={setCreativeContext}
              disabled={isGenerating}
              showSuggestions={!isMobile}
            />

            {!isMobile && (
              <Button
                onClick={handleGenerateClick}
                disabled={!canGenerate}
                className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold glow-primary flex-shrink-0"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {t('studio', 'generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 mr-2" />
                    {t('studio', 'generate')} ({totalCost} {creditWord})
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Right Panel - Output */}
          {isMobile ? (
            (isGenerating || generatedImages.length > 0 || failedFormats.length > 0) && (
              <Collapsible
                open={outputExpanded}
                onOpenChange={setOutputExpanded}
                className="bg-card border border-border rounded-xl overflow-hidden"
              >
                <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors">
                  <h2 className="text-lg font-semibold">{t('studio', 'output')}</h2>
                  <div className="flex items-center gap-2">
                    {generatedImages.length > 0 && (
                      <span className="text-sm text-muted-foreground">
                        {generatedImages.length} {imageWord}
                      </span>
                    )}
                    <ChevronDown 
                      className={cn(
                        "h-5 w-5 transition-transform duration-200",
                        !outputExpanded && "-rotate-90"
                      )} 
                    />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0">
                    <OutputPanel
                      images={generatedImages}
                      isLoading={isGenerating}
                      loadingFormats={loadingFormats}
                      hideTitle
                      sourceFileName={sourceImage?.name}
                      failedFormats={failedFormats}
                      onRetryFormat={handleRetryFormat}
                    />
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )
          ) : (
            <div className="bg-card border border-border rounded-xl p-4 min-h-0 overflow-auto">
              <OutputPanel
                images={generatedImages}
                isLoading={isGenerating}
                loadingFormats={loadingFormats}
                sourceFileName={sourceImage?.name}
                failedFormats={failedFormats}
                onRetryFormat={handleRetryFormat}
              />
            </div>
          )}
        </div>
      </main>

      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-background border-t border-border">
          <Button
            onClick={handleGenerateClick}
            disabled={!canGenerate}
            className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold glow-primary"
          >
            {isGenerating ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                {t('studio', 'generating')}
              </>
            ) : (
              <>
                <Sparkles className="h-5 w-5 mr-2" />
                {t('studio', 'generate')} ({totalCost} {creditWord})
              </>
            )}
          </Button>
        </div>
      )}

      {/* Confirmation dialog for many formats */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('studio', 'confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('studio', 'confirmDescription')
                .replace('{count}', String(selectedFormats.length))
                .replace('{credits}', String(selectedFormats.length))}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('studio', 'confirmCancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => executeGeneration()}>
              {t('studio', 'confirmGenerate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NoCreditsModal
        open={showNoCreditsModal}
        onOpenChange={setShowNoCreditsModal}
        onUpgrade={handleUpgrade}
      />
    </div>
  );
}
